/**
 * Unified Developer Settings Component
 * 
 * Consolidates debug logging and performance monitoring controls into a single panel.
 * Uses only localStorage for settings (no database dependency).
 * All features are tree-shaken away in production builds (NODE_ENV guard).
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Download, Trash2, BarChart3, FileJson, FileText, Bug, Activity, AlertCircle } from "lucide-react";
import { logger } from "@/services/debug-logger";
import { performanceMonitor } from "@/services/performance-monitor";
import { toast } from "@/hooks/use-toast";
// DEBUG_TEMP: Import debug config for production debugging
import { isDebugAllowed } from "@/config/debug-temp";

export function DeveloperSettings() {
  // DEBUG_TEMP: Changed from (process.env.NODE_ENV !== 'development') to use debug flag
  // REVERT: Change back to: if (process.env.NODE_ENV !== 'development') { return null; }
  if (!isDebugAllowed()) {
    return null;
  }

  const [debugEnabled, setDebugEnabled] = useState(() => {
    const config = localStorage.getItem('logger-config');
    if (config) {
      try {
        const parsed = JSON.parse(config);
        return parsed.enabled ?? false;
      } catch {
        return false;
      }
    }
    return false;
  });

  const [performanceEnabled, setPerformanceEnabled] = useState(() => {
    return localStorage.getItem('performanceLoggingEnabled') === 'true';
  });

  const [stats, setStats] = useState(performanceMonitor.getStatistics());
  const [memoryUsage, setMemoryUsage] = useState(performanceMonitor.getMemoryUsage());

  useEffect(() => {
    // Update stats every 5 seconds when performance logging is enabled
    if (performanceEnabled) {
      const interval = setInterval(() => {
        setStats(performanceMonitor.getStatistics());
        setMemoryUsage(performanceMonitor.getMemoryUsage());
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [performanceEnabled]);

  const handleToggleDebug = (enabled: boolean) => {
    setDebugEnabled(enabled);
    logger.updateConfig({ enabled });
    
    toast({
      title: enabled ? "Debug logging enabled" : "Debug logging disabled",
      description: enabled 
        ? "Component renders and system events will now be logged" 
        : "Debug logging has been turned off",
    });
  };

  const handleTogglePerformance = (enabled: boolean) => {
    setPerformanceEnabled(enabled);
    performanceMonitor.setEnabled(enabled);
    
    if (enabled) {
      performanceMonitor.startAllMonitoring();
      toast({
        title: "Performance Monitoring Enabled",
        description: "All performance monitors are now active",
      });
    } else {
      performanceMonitor.stopAllMonitoring();
      toast({
        title: "Performance Monitoring Disabled",
        description: "All performance monitors have been stopped",
      });
    }
  };

  const handleExportJSON = () => {
    const exportData = performanceMonitor.exportAsJSON();
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-analysis-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Export Successful",
      description: "Performance analysis exported as JSON",
    });
  };

  const handleExportCSV = () => {
    const csvData = performanceMonitor.exportAsCSV();
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-logs-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Export Successful",
      description: "Performance logs exported as CSV",
    });
  };

  const handleClearLogs = () => {
    performanceMonitor.clear();
    setStats(performanceMonitor.getStatistics());
    setMemoryUsage(performanceMonitor.getMemoryUsage());
    
    toast({
      title: "Logs Cleared",
      description: "All performance logs have been cleared",
    });
  };

  return (
    <div className="space-y-6">
      {/* Debug Logging Section */}
      <Card className="bg-gray-800 border-gray-600">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Bug className="w-5 h-5 text-purple-400" />
            Debug Logging
          </CardTitle>
          <CardDescription className="text-gray-400">
            Track component renders, view refreshes, and system events
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-600 rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="debug-logging" className="text-white font-medium">
                Enable Debug Logging
              </Label>
              <p className="text-sm text-gray-400">
                Logs will appear in the debug panel
              </p>
            </div>
            <Switch
              id="debug-logging"
              checked={debugEnabled}
              onCheckedChange={handleToggleDebug}
              data-testid="switch-debug-logging"
            />
          </div>

          <div className="text-xs text-gray-500 p-3 bg-gray-900 rounded">
            <p className="font-semibold mb-2">When enabled, logs:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Component lifecycle events (mount, update, unmount)</li>
              <li>Window and dialog state changes</li>
              <li>API calls and responses</li>
              <li>Cache updates and invalidations</li>
            </ul>
            <p className="mt-2 text-yellow-400 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              May impact performance when enabled
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Performance Monitoring Section */}
      <Card className="bg-gray-800 border-gray-600">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Activity className="w-5 h-5 text-blue-400" />
            Performance Monitoring
          </CardTitle>
          <CardDescription className="text-gray-400">
            Track API calls, render times, Web Vitals, and system metrics
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-600 rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="performance-logging" className="text-white font-medium">
                Enable Performance Monitoring
              </Label>
              <p className="text-sm text-gray-400">
                Comprehensive performance tracking with AI insights
              </p>
            </div>
            <Switch
              id="performance-logging"
              checked={performanceEnabled}
              onCheckedChange={handleTogglePerformance}
              data-testid="switch-performance-logging"
            />
          </div>

          {performanceEnabled && (
            <>
              {/* Statistics Dashboard */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-900 rounded-lg">
                  <div className="text-xs text-gray-400 mb-1">Total Logs</div>
                  <div className="text-2xl font-bold text-white">{stats.totalLogs.toLocaleString()}</div>
                </div>
                <div className="p-3 bg-gray-900 rounded-lg">
                  <div className="text-xs text-gray-400 mb-1">Error Rate</div>
                  <div className="text-2xl font-bold text-white">{stats.errorRate.toFixed(1)}%</div>
                </div>
                <div className="p-3 bg-gray-900 rounded-lg">
                  <div className="text-xs text-gray-400 mb-1">Cache Hit Rate</div>
                  <div className="text-2xl font-bold text-white">{stats.cacheHitRate.toFixed(1)}%</div>
                </div>
                <div className="p-3 bg-gray-900 rounded-lg">
                  <div className="text-xs text-gray-400 mb-1">Anomalies</div>
                  <div className="text-2xl font-bold text-white">{stats.anomalyCount}</div>
                </div>
              </div>

              {/* Performance Ratings */}
              <div className="p-3 bg-gray-900 rounded-lg">
                <div className="text-xs text-gray-400 mb-2">Performance Ratings</div>
                <div className="grid grid-cols-5 gap-2 text-center">
                  <div>
                    <div className="text-xs text-green-400">Excellent</div>
                    <div className="text-sm font-bold text-white">{stats.performanceRatings.excellent}</div>
                  </div>
                  <div>
                    <div className="text-xs text-blue-400">Good</div>
                    <div className="text-sm font-bold text-white">{stats.performanceRatings.good}</div>
                  </div>
                  <div>
                    <div className="text-xs text-yellow-400">Acceptable</div>
                    <div className="text-sm font-bold text-white">{stats.performanceRatings.acceptable}</div>
                  </div>
                  <div>
                    <div className="text-xs text-orange-400">Slow</div>
                    <div className="text-sm font-bold text-white">{stats.performanceRatings.slow}</div>
                  </div>
                  <div>
                    <div className="text-xs text-red-400">Critical</div>
                    <div className="text-sm font-bold text-white">{stats.performanceRatings.critical}</div>
                  </div>
                </div>
              </div>

              {/* Memory Usage */}
              {memoryUsage && (
                <div className="p-3 bg-gray-900 rounded-lg">
                  <div className="text-xs text-gray-400 mb-2">Memory Usage</div>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm text-white">{memoryUsage.usedMB} MB / {memoryUsage.limitMB} MB</div>
                      <div className="text-xs text-gray-400">Used / Limit</div>
                    </div>
                    <div className="text-2xl font-bold text-white">{memoryUsage.usagePercentage}%</div>
                  </div>
                  <div className="mt-2 w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        memoryUsage.usagePercentage > 90 ? 'bg-red-500' :
                        memoryUsage.usagePercentage > 75 ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(memoryUsage.usagePercentage, 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Log Type Breakdown */}
              {Object.keys(stats.byType).length > 0 && (
                <div className="p-3 bg-gray-900 rounded-lg">
                  <div className="text-xs text-gray-400 mb-2">Log Types</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {Object.entries(stats.byType).map(([type, count]) => (
                      <div key={type} className="flex justify-between">
                        <span className="text-gray-400 capitalize">{type}:</span>
                        <span className="text-white font-mono">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Export and Clear Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={handleExportJSON}
                  size="sm"
                  variant="outline"
                  className="flex-1 bg-gray-700 border-gray-600 hover:bg-gray-600"
                  data-testid="button-export-json"
                >
                  <FileJson className="w-4 h-4 mr-2" />
                  Export JSON
                </Button>
                <Button
                  onClick={handleExportCSV}
                  size="sm"
                  variant="outline"
                  className="flex-1 bg-gray-700 border-gray-600 hover:bg-gray-600"
                  data-testid="button-export-csv"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button
                  onClick={handleClearLogs}
                  size="sm"
                  variant="outline"
                  className="flex-1 bg-gray-700 border-gray-600 hover:bg-gray-600 text-red-400 hover:text-red-300"
                  data-testid="button-clear-logs"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear Logs
                </Button>
              </div>
            </>
          )}

          <div className="text-xs text-gray-500 p-3 bg-gray-900 rounded">
            <p className="font-semibold mb-2">Monitoring includes:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>API fetch performance with baselines & AI annotations</li>
              <li>React Query cache operations</li>
              <li>Web Vitals (LCP, FID, CLS)</li>
              <li>Component render times with anomaly detection</li>
              <li>Memory usage & leak detection</li>
              <li>Network connectivity & connection quality</li>
              <li>Long task detection (main thread blocking)</li>
              <li>User interactions (click, scroll, input)</li>
              <li>Error tracking with comprehensive context</li>
            </ul>
            <p className="mt-2 text-blue-400 flex items-center gap-1">
              <BarChart3 className="w-3 h-3" />
              All data stored in localStorage with statistical baselines
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Production Build Info */}
      <div className="p-4 bg-yellow-900/20 border border-yellow-600/30 rounded-lg">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-yellow-200 mb-1">Production Build Note</p>
            <p className="text-yellow-100">
              All logging and monitoring code is <strong>completely tree-shaken away</strong> in production builds.
              Production has <strong>ZERO performance overhead</strong> from this developer tooling.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
